import { useState } from 'react'

import { Box, BoxList, Row, Title } from '@factorialco/deprecated-design-system'
import { ContractSettingsMaterializedTemplateFragmentFragment } from 'generated/resources/graphql'
import i18n from 'modules/core/lib/i18n'
import SelectField from 'modules/core/components/SelectField'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { countryLabel } from 'modules/contracts_settings/components/ContractFields/Taxonomies/taxonomyTranslator'
import CountryField from 'modules/contracts_settings/components/CountrySpecificData/CountryField'

type Props = {
  materializedCountryTemplateFragments: ContractSettingsMaterializedTemplateFragmentFragment[]
  showAdditionalCompensationField: boolean
}

const formatCountrySpecificFieldName = (fieldName: string) => {
  return fieldName
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (character) => character.toUpperCase())
}

const CountrySpecificData = ({
  materializedCountryTemplateFragments,
  showAdditionalCompensationField,
}: Props) => {
  const showSelector = materializedCountryTemplateFragments.length > 1
  const [selectedTemplate, setSelectedTemplate] = useState<
    ContractSettingsMaterializedTemplateFragmentFragment | undefined
  >(materializedCountryTemplateFragments[0])

  const contractTemplatesOptions = useUnsafeMemo(() => {
    return materializedCountryTemplateFragments.map((template) => ({
      label: countryLabel(template.countryCode || ''),
      value: template.countryCode,
    }))
  }, [materializedCountryTemplateFragments])

  useUnsafeEffect(() => {
    if (selectedTemplate) {
      const updated = materializedCountryTemplateFragments.find(
        (template) => template.countryCode === selectedTemplate.countryCode
      )
      if (updated) {
        setSelectedTemplate(updated)
      }
    }
  }, [materializedCountryTemplateFragments])

  const handleTemplateChange = (value: string) => {
    const targetTemplate = materializedCountryTemplateFragments.find(
      (template) => template.countryCode === value
    )
    setSelectedTemplate(targetTemplate)
  }

  if (!selectedTemplate) return null

  return (
    <Box flexGrow width="full">
      <Row>
        <Row.Col halfWidth>
          <Box paddingRight="s64">
            <Title
              variant="500"
              illustration="contract-handshake"
              title={i18n.t('company_settings.general.employment_data.contract_taxonomies.title')}
              description={i18n.t(
                'company_settings.general.employment_data.contract_taxonomies.description'
              )}
            ></Title>
          </Box>
        </Row.Col>
        <Row.Col halfWidth>
          <Box flexDirection="column" gap="s16">
            <Box maxWidth="fit-content">
              {showSelector && (
                <SelectField
                  options={contractTemplatesOptions}
                  value={selectedTemplate.countryCode || undefined}
                  onChange={handleTemplateChange}
                />
              )}
            </Box>
            <BoxList>
              {selectedTemplate.templateFragment.map((countryField) => (
                <CountryField
                  key={`countryField-${countryField.id}`}
                  name={formatCountrySpecificFieldName(countryField.label)}
                  values={countryField.options ?? []}
                  fragmentId={selectedTemplate.id}
                  fieldId={countryField.fieldId}
                />
              ))}
            </BoxList>
          </Box>
        </Row.Col>
      </Row>
    </Box>
  )
}

export default CountrySpecificData
